import { createContext, useContext, useId } from "react";

export interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

interface FieldControlOptions {
  id?: string;
  invalid?: boolean;
  inlineLabel?: string;
  describedBy?: string;
}

/**
 * Id and aria wiring of a text control: its own id, else the surrounding Field's, else a
 * generated one when an inline label has to point at it.
 */
export function useFieldControl({ id, invalid, inlineLabel, describedBy }: FieldControlOptions): {
  id: string | undefined;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
} {
  const field = useFieldContext();
  const ownId = useId();
  return {
    id: id ?? field?.id ?? (inlineLabel ? ownId : undefined),
    "aria-invalid": invalid || field?.invalid || undefined,
    "aria-describedby": describedBy ?? field?.describedBy,
  };
}
